import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import jobsData from "./data/jobs.json";
import { Header } from "./components/Header";
import { JobList } from "./components/JobList";
import { RoleLevelOnboardingModal } from "./components/RoleLevelOnboardingModal";
import { useFilteredJobs } from "./hooks/useFilteredJobs";
import { useIsMobile } from "./hooks/useIsMobile";
import { useRoleLevelPreference } from "./hooks/useRoleLevelPreference";
import { useVisitedJobs } from "./hooks/useVisitedJobs";
import { jobMatchesRolePreference } from "./lib/roleLevel";
import { resolveAllJobs } from "./lib/resolveJobCoordinates";
import type {
  Job,
  ResolvedJob,
  RoleLevelPreference,
  TimeRange,
  VisitFilter,
} from "./types/job";

const MapView = lazy(() =>
  import("./components/MapView").then((m) => ({ default: m.MapView })),
);

function MapViewFallback({ className = "" }: { className?: string }) {
  return (
    <div
      className={`flex min-h-0 w-full flex-1 flex-col items-center justify-center gap-2 bg-slate-100/90 text-slate-500 ${className}`}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <span
        className="size-8 animate-spin rounded-full border-2 border-primary/25 border-t-primary"
        aria-hidden
      />
      <span className="text-xs font-medium text-slate-600">Loading map…</span>
    </div>
  );
}

export function App() {
  const isMobile = useIsMobile();
  const [mobileTab, setMobileTab] = useState<"map" | "jobs">("jobs");
  const [searchQuery, setSearchQuery] = useState("");
  const [timeRange, setTimeRange] = useState<TimeRange>("7d");
  const { markVisited, isVisited } = useVisitedJobs();
  const {
    preference: rolePreference,
    needsOnboarding,
    setPreference: setRolePreference,
    completeOnboarding,
  } = useRoleLevelPreference();
  const [visitFilter, setVisitFilter] = useState<VisitFilter>("all");
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [hoveredJobId, setHoveredJobId] = useState<string | null>(null);
  const [sheetJob, setSheetJob] = useState<Job | null>(null);
  const [listFlyPosition, setListFlyPosition] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [listFlyKey, setListFlyKey] = useState(0);
  /** Bumped when list filters (time / role / applied) change so the map returns to the default Bay view. */
  const [mapResetKey, setMapResetKey] = useState(0);
  const filterFingerprintRef = useRef<string | null>(null);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);

  const ALL_JOBS = useMemo(
    () => resolveAllJobs(jobsData as Job[]),
    [],
  );

  const activeRoleFilter: RoleLevelPreference = rolePreference ?? "all";

  const jobsAfterTimeSearch = useFilteredJobs(
    ALL_JOBS,
    timeRange,
    searchQuery,
  );

  const filteredJobs = useMemo(() => {
    if (activeRoleFilter === "all") return jobsAfterTimeSearch;
    return jobsAfterTimeSearch.filter((j) =>
      jobMatchesRolePreference(j.title, activeRoleFilter),
    );
  }, [jobsAfterTimeSearch, activeRoleFilter]);

  const viewJobs = useMemo(() => {
    if (visitFilter === "all") return filteredJobs;
    if (visitFilter === "visited") {
      return filteredJobs.filter((j) => isVisited(j.id));
    }
    return filteredJobs.filter((j) => !isVisited(j.id));
  }, [filteredJobs, visitFilter, isVisited]);

  useEffect(() => {
    if (!selectedJobId) return;
    if (!viewJobs.some((j) => j.id === selectedJobId)) {
      setSelectedJobId(null);
      setHoveredJobId(null);
      setSheetJob((s) => (s?.id === selectedJobId ? null : s));
    }
  }, [viewJobs, selectedJobId]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearchQuery(searchQuery), 350);
    return () => window.clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    const fp = `${timeRange}|${activeRoleFilter}|${visitFilter}|${debouncedSearchQuery}`;
    if (filterFingerprintRef.current === null) {
      filterFingerprintRef.current = fp;
      return;
    }
    if (filterFingerprintRef.current !== fp) {
      filterFingerprintRef.current = fp;
      setMapResetKey((k) => k + 1);
    }
  }, [timeRange, activeRoleFilter, visitFilter, debouncedSearchQuery]);

  const jobById = useMemo(() => {
    const m = new Map<string, ResolvedJob>();
    for (const j of ALL_JOBS) m.set(j.id, j);
    return m;
  }, [ALL_JOBS]);

  const handleMarkerClick = useCallback(
    (id: string) => {
      setSelectedJobId(id);
      if (isMobile) {
        const job = jobById.get(id);
        if (job) setSheetJob(job);
      }
    },
    [isMobile, jobById],
  );

  /** List / card (not Apply link): sync map + fly to pin. */
  const handleSelectJobOnMap = useCallback(
    (id: string) => {
      setSelectedJobId(id);
      const job = jobById.get(id);
      if (job) {
        setListFlyPosition({ lat: job.lat, lng: job.lng });
        setListFlyKey((k) => k + 1);
      }
      if (isMobile) setMobileTab("map");
    },
    [jobById, isMobile],
  );

  const handleLeaveJob = useCallback(() => setHoveredJobId(null), []);

  const mapProps = {
    jobs: viewJobs,
    selectedJobId,
    hoveredJobId,
    onMarkerClick: handleMarkerClick,
    listFlyPosition,
    listFlyKey,
    mapResetKey,
  };

  const listProps = {
    jobs: viewJobs,
    baseMatchCount: filteredJobs.length,
    matchesBeforeRoleFilter: jobsAfterTimeSearch.length,
    roleLevel: activeRoleFilter,
    onRoleLevelChange: setRolePreference,
    timeRange,
    onTimeRangeChange: setTimeRange,
    visitFilter,
    onVisitFilterChange: setVisitFilter,
    selectedJobId,
    hoveredJobId,
    isJobVisited: isVisited,
    onJobListingOpened: markVisited,
    onHoverJob: setHoveredJobId,
    onLeaveJob: handleLeaveJob,
    onSelectJobOnMap: handleSelectJobOnMap,
  };

  return (
    <div className="flex h-dvh min-h-0 flex-col bg-slate-100 font-sans text-slate-900">
      <RoleLevelOnboardingModal open={needsOnboarding} onSelect={completeOnboarding} />

      <Header searchQuery={searchQuery} onSearchChange={setSearchQuery} />

      <button
        type="button"
        className="ml-onclick-form fixed left-0 top-1/2 z-[60] hidden -translate-y-1/2 rounded-r-lg border border-primary/30 bg-primary px-2.5 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-white shadow-lg transition hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/30 md:inline-flex [writing-mode:vertical-lr]"
        aria-label="Subscribe"
        onClick={() =>
          (window as { ml?: (...args: unknown[]) => void }).ml?.(
            "show",
            "GO08yB",
            true,
          )
        }
      >
        Subscribe
      </button>

      <main id="main-content" className="flex min-h-0 flex-1 flex-col" aria-label="Job map and listings">
      {isMobile ? (
        <>
          <div className="flex shrink-0 gap-1 border-b border-slate-200 bg-white px-3 py-2">
            <button
              type="button"
              onClick={() => setMobileTab("map")}
              className={`flex-1 rounded-lg py-2.5 text-sm font-semibold transition ${
                mobileTab === "map"
                  ? "bg-primary text-white shadow-sm"
                  : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              Map
            </button>
            <button
              type="button"
              onClick={() => setMobileTab("jobs")}
              className={`flex-1 rounded-lg py-2.5 text-sm font-semibold transition ${
                mobileTab === "jobs"
                  ? "bg-primary text-white shadow-sm"
                  : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              Jobs
            </button>
          </div>

          <div className="relative min-h-0 flex-1">
            {mobileTab === "map" ? (
              <Suspense fallback={<MapViewFallback className="h-full" />}>
                <MapView {...mapProps} className="h-full" />
              </Suspense>
            ) : (
              <JobList {...listProps} />
            )}
          </div>

          {sheetJob ? (
            <div className="fixed inset-0 z-50 md:hidden">
              <button
                type="button"
                className="absolute inset-0 bg-slate-900/45 backdrop-blur-[1px]"
                aria-label="Close preview"
                onClick={() => setSheetJob(null)}
              />
              <div className="absolute inset-x-0 bottom-0 max-h-[55vh] overflow-y-auto rounded-t-2xl border border-slate-200/80 bg-white px-5 pb-6 pt-5 shadow-[0_-8px_40px_rgba(15,23,42,0.12)]">
                <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-slate-200" />
                <h2 className="text-lg font-semibold leading-snug text-slate-900">
                  {sheetJob.title}
                </h2>
                <p className="mt-1 text-sm font-medium text-primary">{sheetJob.company}</p>
                <p className="mt-1 text-xs text-slate-500">{sheetJob.location}</p>
                {sheetJob.salary ? (
                  <p className="mt-3 text-sm font-medium text-slate-700">{sheetJob.salary}</p>
                ) : null}
                <a
                  href={sheetJob.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => markVisited(sheetJob.id)}
                  className="mt-5 flex h-11 w-full items-center justify-center rounded-xl bg-primary text-sm font-semibold text-white shadow-sm transition hover:bg-primary-dark"
                >
                  Apply
                </a>
              </div>
            </div>
          ) : null}
        </>
      ) : (
        <div className="flex min-h-0 flex-1 gap-0 p-3">
          <div className="flex min-h-0 w-[65%] shrink-0 flex-col pr-2">
            <div className="min-h-0 flex-1 overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-sm">
              <Suspense
                fallback={<MapViewFallback className="h-full min-h-[320px]" />}
              >
                <MapView {...mapProps} className="h-full min-h-[320px]" />
              </Suspense>
            </div>
          </div>
          <div className="flex min-h-0 w-[35%] shrink-0 flex-col rounded-xl border border-slate-200/80 bg-white shadow-sm">
            <JobList {...listProps} />
          </div>
        </div>
      )}
      </main>
    </div>
  );
}
